import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, GripVertical } from "lucide-react";

// Two side-by-side panes with a draggable centre line. Dragging (or the
// arrow keys on the focused line) resizes the panes; dragging close to an
// edge collapses that pane so the other one takes the full width. The arrow
// buttons on the line step between "left maximised", the default split and
// "right maximised". Double-click (or Enter) restores the default split.

const COLLAPSE_AT = 8; // % from an edge at which a pane snaps shut
const KEY_STEP = 2;
const KEY_STEP_LARGE = 10;

// Matches the stylesheet breakpoint where the panes stack and the line hides.
const STACKED_QUERY = "(max-width: 900px)";

const clampPercent = (value) => Math.min(100, Math.max(0, value));

const useIsStacked = () => {
  const getMatch = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(STACKED_QUERY).matches
      : false;
  const [isStacked, setIsStacked] = useState(getMatch);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia(STACKED_QUERY);
    const handleChange = () => setIsStacked(query.matches);
    handleChange();
    query.addEventListener?.("change", handleChange);
    return () => query.removeEventListener?.("change", handleChange);
  }, []);

  return isStacked;
};

const snapPercent = (value) => {
  if (value < COLLAPSE_AT) return 0;
  if (value > 100 - COLLAPSE_AT) return 100;
  return Math.round(value * 10) / 10;
};

const readStoredPercent = (storageKey, fallback) => {
  if (!storageKey) return fallback;
  try {
    const stored = Number.parseFloat(window.localStorage.getItem(storageKey));
    return Number.isFinite(stored) ? clampPercent(stored) : fallback;
  } catch (error) {
    return fallback;
  }
};

export default function ResizableSplit({
  left,
  right,
  leftLabel,
  rightLabel,
  defaultLeftPercent = 25,
  storageKey,
}) {
  const containerRef = useRef(null);
  const [leftPercent, setLeftPercent] = useState(() =>
    readStoredPercent(storageKey, defaultLeftPercent)
  );
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, String(leftPercent));
    } catch (error) {
      // Storage can be unavailable (private mode); the split still works.
    }
  }, [leftPercent, storageKey]);

  useEffect(() => {
    if (!isDragging) return undefined;
    document.body.classList.add("ads-split-dragging");
    return () => document.body.classList.remove("ads-split-dragging");
  }, [isDragging]);

  const percentFromPointer = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    return snapPercent(clampPercent(((clientX - rect.left) / rect.width) * 100));
  }, []);

  const handlePointerDown = (event) => {
    if (event.button !== 0 || event.target.closest("button")) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    if (!isDragging) return;
    const next = percentFromPointer(event.clientX);
    if (next !== null) setLeftPercent(next);
  };

  const stopDragging = (event) => {
    if (!isDragging) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setIsDragging(false);
  };

  // The arrow buttons step between these positions of the centre line.
  const stops = [0, defaultLeftPercent, 100];
  const stepLeftTarget = [...stops].reverse().find((stop) => stop < leftPercent - 0.5) ?? 0;
  const stepRightTarget = stops.find((stop) => stop > leftPercent + 0.5) ?? 100;
  const stepLeftLabel = stepLeftTarget === 0 ? `Maximise ${rightLabel}` : "Restore default split";
  const stepRightLabel = stepRightTarget === 100 ? `Maximise ${leftLabel}` : "Restore default split";

  const handleKeyDown = (event) => {
    const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
    const keyActions = {
      ArrowLeft: () => setLeftPercent((value) => snapPercent(clampPercent(value - step))),
      ArrowRight: () => setLeftPercent((value) => snapPercent(clampPercent(value + step))),
      Home: () => setLeftPercent(0),
      End: () => setLeftPercent(100),
      Enter: () => setLeftPercent(defaultLeftPercent),
    };
    const action = keyActions[event.key];
    if (action) {
      event.preventDefault();
      action();
    }
  };

  // Stacked (narrow screens) shows both panes in full, so nothing is collapsed.
  const isStacked = useIsStacked();
  const leftCollapsed = !isStacked && leftPercent === 0;
  const rightCollapsed = !isStacked && leftPercent === 100;

  return (
    <div
      ref={containerRef}
      className={`ads-split${isDragging ? " is-dragging" : ""}`}
      style={{ "--ads-left-width": `${leftPercent}%` }}
    >
      <div
        className={`ads-split-pane ads-split-pane-left${leftCollapsed ? " is-collapsed" : ""}`}
        aria-hidden={leftCollapsed || undefined}
      >
        {left}
      </div>

      {/* The whole line is draggable; the focusable grip is the separator
          (kept apart from the buttons, whose role would be flattened inside it). */}
      <div
        className="ads-split-divider"
        title="Drag to resize. Double-click to reset."
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        onDoubleClick={() => setLeftPercent(defaultLeftPercent)}
      >
        <button
          type="button"
          className="ads-split-step"
          onClick={() => setLeftPercent(stepLeftTarget)}
          disabled={leftCollapsed}
          aria-label={stepLeftLabel}
          title={stepLeftLabel}
        >
          <ChevronLeft size={14} aria-hidden="true" />
        </button>
        <span
          className="ads-split-grip"
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${leftLabel} and ${rightLabel}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(leftPercent)}
          aria-valuetext={`${leftLabel} ${Math.round(leftPercent)}%, ${rightLabel} ${Math.round(100 - leftPercent)}%`}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <GripVertical size={14} aria-hidden="true" />
        </span>
        <button
          type="button"
          className="ads-split-step"
          onClick={() => setLeftPercent(stepRightTarget)}
          disabled={rightCollapsed}
          aria-label={stepRightLabel}
          title={stepRightLabel}
        >
          <ChevronRight size={14} aria-hidden="true" />
        </button>
      </div>

      <div
        className={`ads-split-pane ads-split-pane-right${rightCollapsed ? " is-collapsed" : ""}`}
        aria-hidden={rightCollapsed || undefined}
      >
        {right}
      </div>
    </div>
  );
}
